'use client';

import type { FC } from 'react';
import type { UnitThumbnailProps } from '@/app/_components/feature/product-unit/ui/unit-thumbnail';
import type { UnitKeyInfoProps } from '@/app/_components/feature/product-unit/ui/unit-key-info';
import type { UnitInfoProps } from '@/app/_components/feature/product-unit/ui/unit-info';
import type { BenefitBadgeProps } from '@/app/_components/feature/product-unit/ui/unit-benefit-badge';
import type { UnitPriceProps } from '@/app/_components/feature/product-unit/ui/unit-price';
import type { UnitFeatureProps } from '@/app/_components/feature/product-unit/ui/unit-feature';
import { useCallback, useState, useEffect, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { UnitCardBenefitBadge, UnitBenefitBadge } from '@/app/_components/feature/product-unit/ui/unit-benefit-badge';
import { UnitCardPrice, UnitPrice } from '@/app/_components/feature/product-unit/ui/unit-price';
import { UnitCardInfo, UnitInfo } from '@/app/_components/feature/product-unit/ui/unit-info';
import { UnitCardKeyInfo, UnitKeyInfo } from '@/app/_components/feature/product-unit/ui/unit-key-info';
import { UnitThumbnail } from '@/app/_components/feature/product-unit/ui/unit-thumbnail';
import { UnitFeature } from '@/app/_components/feature/product-unit/ui/unit-feature';
import { cn } from '@/app/_shared/utils/tw-cn';
import useVisibilityImpression from '@/app/_shared/hooks/use-visibility-impression';

/**
 * 디자인 설계 참고 피그마
 * @see {@link https://www.figma.com/design/IxDIbEO9tR3tXThxQ6TTNg/UPD-1622-PLP%2FSRP-%EB%94%94%EC%9E%90%EC%9D%B8%EA%B0%9C%ED%8E%B8-%EB%B0%8F-%ED%95%98%EC%9D%B4%EB%B8%8C%EB%A6%AC%EB%93%9C-%EC%A0%84%ED%99%98?node-id=788-316129&m=dev}
 * */

export const ProductUnitType = {
  BANNER: 'BANNER',
  LIST: 'LIST',
  CARD: 'CARD',
} as const;

export interface ProductUnitProps {
  url?: string;
  type: keyof typeof ProductUnitType;
  thumbnail: UnitThumbnailProps;
  keyInfo: UnitKeyInfoProps;
  info: UnitInfoProps;
  benefitBadge: BenefitBadgeProps;
  price: UnitPriceProps;
  feature: UnitFeatureProps;
  onItemClick?: () => void; // 클릭이벤트 ( 웹뷰 or 웹 페이지 이동, 클라이언트 로그 등)
  onImpression?: () => void; // 노출 이벤트
}

export const ProductUnitList: FC<Omit<ProductUnitProps, 'type'>> = (props) => {
  const { url, thumbnail, keyInfo, info, benefitBadge, price, feature, onImpression, onItemClick } = props;

  const { ref } = useVisibilityImpression({
    onImpression: () => {
      onImpression?.();
    },
    enableVisibilityChange: yaNative.isEnabled,
  });

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onItemClick?.();
  }, []);

  return (
    <a ref={ref} href={url} className="flex w-full min-w-[320px] flex-col p-16 pc:w-1/2" onClick={handleClick}>
      <div className="grid w-full grid-cols-[126px_1fr] gap-[12px]">
        {/* 좌상단 썸네일 영역 */}
        <UnitThumbnail {...thumbnail} ratio="3_4" />
        {/* 우상단 컨텐트 영역 */}
        <div className="flex flex-col overflow-hidden">
          <UnitKeyInfo {...keyInfo} />
          <UnitInfo {...info} />
          <UnitBenefitBadge {...benefitBadge} />
          <UnitPrice {...price} />
        </div>
      </div>
      {/* 하단 영역 */}
      <UnitFeature {...feature} />
    </a>
  );
};

export const ProductUnitBanner: FC<Omit<ProductUnitProps, 'type'>> = (props) => {
  const { url, thumbnail, keyInfo, info, benefitBadge, price, feature, onImpression, onItemClick } = props;
  const { ref } = useVisibilityImpression({
    onImpression: () => {
      onImpression?.();
    },
    enableVisibilityChange: yaNative.isEnabled,
  });

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onItemClick?.();
  }, []);

  return (
    <a ref={ref} href={url} className="flex w-full min-w-[320px] flex-col p-16 pc:w-1/2" onClick={handleClick}>
      <div className="grid w-full grid-flow-row gap-[12px]">
        {/* 좌상단 썸네일 영역 */}
        <UnitThumbnail {...thumbnail} ratio="2_1" />
        {/* 우상단 컨텐트 영역 */}
        <div className="flex flex-col overflow-hidden">
          <UnitKeyInfo {...keyInfo} />
          <UnitInfo {...info} />
          <UnitBenefitBadge {...benefitBadge} />
          <UnitPrice {...price} />
        </div>
      </div>
      {/* 하단 영역 */}
      <UnitFeature {...feature} />
    </a>
  );
};

export const ProductUnitCard: FC<Omit<ProductUnitProps, 'type'>> = (props) => {
  const { thumbnail, url, keyInfo, info, benefitBadge, price, onImpression, onItemClick } = props;
  const ref = useRef<HTMLAnchorElement>(null);
  const [cardMinHeight, setCardMinHeight] = useState<number>(150);

  useEffect(() => {
    if (ref?.current && ref?.current?.clientHeight > 150) {
      setCardMinHeight(170);
    }
  }, [ref]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onItemClick?.();
  }, []);

  return (
    <InView className="flex w-full justify-center" triggerOnce onChange={(inView) => inView && onImpression?.()}>
      <a
        ref={ref}
        href={url}
        className={cn(
          'flex min-h-[150px] w-full min-w-[288px] rounded-12 bg-static-white shadow-3 legacy-pc:w-[328px]',
          {
            'min-h-[170px]': cardMinHeight > 150,
          },
        )}
        onClick={handleClick}>
        <div className="grid w-full grid-cols-[96px_1fr] gap-[10px] p-10">
          {/* 좌상단 썸네일 영역 */}
          <UnitThumbnail {...thumbnail} />
          {/* 우상단 컨텐트 영역 */}
          <div className="flex flex-col overflow-hidden">
            <UnitCardKeyInfo {...keyInfo} />
            <UnitCardInfo {...info} />
            <UnitCardBenefitBadge {...benefitBadge} />
            <UnitCardPrice {...price} />
          </div>
        </div>
      </a>
    </InView>
  );
};

export const ProductUnit = (props: ProductUnitProps) => {
  switch (props.type) {
    case ProductUnitType.BANNER:
      return <ProductUnitBanner {...props} />;
    case ProductUnitType.LIST:
      return <ProductUnitList {...props} />;
    case ProductUnitType.CARD:
      return <ProductUnitCard {...props} />;
    default:
      return null;
  }
};
